'use client';

import { ReportingCenter } from './reportingCenter';
import { ProviderType, type TrackingEvent } from './types';

/**
 * TECH-26-151 身份链路诊断：只记录状态和时序，绝不记录 token、visitor-id、邮箱、完整 URL 或请求体。
 * TECH-26-151 固定抽样 10% 页面会话。
 */
const TOKEN_KEY = 'zl-token';
const VISITOR_KEY = 'zl-visitor_id';
const MAX_LIFECYCLE_EVENTS = 40;

type IdentityKey = typeof TOKEN_KEY | typeof VISITOR_KEY;
type IdentityName = 'token' | 'visitor_id';
type Presence = 'present' | 'missing' | 'read_error';
type DiagnosticProperties = Record<string, string | number | boolean | undefined>;

interface IdentityRequestSnapshot {
  requestId: string;
  endpoint: string;
  tokenStorage: Presence;
  visitorStorage: Presence;
  authorizationPrepared: boolean;
  visitorHeaderPrepared: boolean;
}

interface IdentityDiagnosticsOptions {
  sampleRate: number;
  release: string;
  site: string;
}

let initialized = false;
let selected = false;
let lifecycleCount = 0;
let sequence = 0;
let options: IdentityDiagnosticsOptions = { sampleRate: 0, release: 'unknown', site: 'unknown' };
const firstAnomalies = new Set<string>();
let originalSetItem: Storage['setItem'] | null = null;
let originalRemoveItem: Storage['removeItem'] | null = null;
let originalClear: Storage['clear'] | null = null;

declare global {
  interface Window {
    /** 紧急停用抽样会话的运行时开关。 */
    __ZL_IDENTITY_DIAGNOSTICS_DISABLED?: boolean;
  }
}

function isDisabled(): boolean {
  return typeof window === 'undefined' || window.__ZL_IDENTITY_DIAGNOSTICS_DISABLED === true;
}

function identityName(key: IdentityKey): IdentityName {
  return key === TOKEN_KEY ? 'token' : 'visitor_id';
}

function safePathname(): string {
  try {
    return typeof window === 'undefined' ? '/' : window.location?.pathname || '/';
  } catch {
    return '/';
  }
}

function safeOrigin(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location?.origin || '';
  } catch {
    return '';
  }
}

function baseProperties(): DiagnosticProperties {
  return {
    diag_release: options.release,
    diag_site: options.site,
    diag_path: safePathname(),
    diag_sequence: sequence++,
    diag_elapsed_ms: typeof performance === 'undefined' ? 0 : Math.round(performance.now()),
  };
}

function dispatch(event: TrackingEvent): void {
  if (!selected || isDisabled()) return;
  try {
    // 统一走 ReportingCenter：指定 Sensors 后，既有镜像逻辑会同时发送到 Snowplow。
    ReportingCenter.getInstance().track(event, [ProviderType.SENSORS]);
  } catch {
    // 诊断传输失败不能影响身份写入、请求发送或登录状态。
  }
}

function record(
  eventName: 'WebIdentityLifecycle' | 'WebIdentityDiagnostic' | 'WebIdentityDiagnosticContext',
  properties: DiagnosticProperties,
  lifecycle = false,
): void {
  // 未命中抽样的调用必须是严格 no-op，不能接触浏览器对象或改变业务异常语义。
  if (!selected || isDisabled()) return;
  if (lifecycle && lifecycleCount++ >= MAX_LIFECYCLE_EVENTS) return;
  dispatch({
    eventName,
    properties: {
      ...baseProperties(),
      ...properties,
      // 避免 SensorsProvider 的完整当前地址（可能含 query）覆盖诊断地址。
      $url: safeOrigin(),
    },
  });
}

function recordFirstAnomaly(reason: string, properties: DiagnosticProperties): void {
  if (firstAnomalies.has(reason)) return;
  firstAnomalies.add(reason);
  record('WebIdentityDiagnostic', { diag_reason: reason, ...properties });
}

function isIdentityKey(key: string): key is IdentityKey {
  return key === TOKEN_KEY || key === VISITOR_KEY;
}

/** 在全局客户端初始化时设置抽样，返回本页是否已命中。 */
export function startIdentityDiagnostics(nextOptions: IdentityDiagnosticsOptions): boolean {
  if (initialized) return selected;
  initialized = true;
  options = nextOptions;
  if (typeof window === 'undefined' || isDisabled()) return false;

  selected = nextOptions.sampleRate > 0 && Math.random() < nextOptions.sampleRate;
  if (!selected) return false;
  record('WebIdentityLifecycle', { diag_stage: 'client_started' }, true);
  installStorageProbe();
  return true;
}

/** 读取身份存储并保留调用方原有的异常语义。 */
export function readIdentityStorage(key: IdentityKey, source: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    record(
      'WebIdentityLifecycle',
      {
        diag_stage: 'storage_read',
        diag_identity: identityName(key),
        diag_source: source,
        diag_presence: value ? 'present' : 'missing',
      },
      true,
    );
    return value;
  } catch (error) {
    recordFirstAnomaly('storage_read_error', {
      diag_identity: identityName(key),
      diag_source: source,
    });
    throw error;
  }
}

/** 记录初始化阶段；调用方继续执行原有 JSON.parse 和存储逻辑。 */
export function recordIdentityLifecycle(
  stage: string,
  properties: DiagnosticProperties = {},
): void {
  record('WebIdentityLifecycle', { diag_stage: stage, ...properties }, true);
}

export function recordIdentityError(stage: string): void {
  recordFirstAnomaly('identity_init_error', { diag_stage: stage });
}

/** 在 Axios 拦截器组装请求头后建立不含身份值的请求快照。 */
export function createIdentityRequestSnapshot(input: {
  endpoint: string | undefined;
  tokenStorage: Presence;
  visitorStorage: Presence;
  authorizationPrepared: boolean;
  visitorHeaderPrepared: boolean;
}): IdentityRequestSnapshot {
  const snapshot: IdentityRequestSnapshot = {
    requestId: `identity-${Date.now()}-${sequence++}`,
    endpoint: input.endpoint || 'unknown',
    tokenStorage: input.tokenStorage,
    visitorStorage: input.visitorStorage,
    authorizationPrepared: input.authorizationPrepared,
    visitorHeaderPrepared: input.visitorHeaderPrepared,
  };
  record(
    'WebIdentityLifecycle',
    {
      diag_stage: 'request_prepared',
      diag_request_id: snapshot.requestId,
      diag_endpoint: snapshot.endpoint,
      diag_token_storage: snapshot.tokenStorage,
      diag_visitor_storage: snapshot.visitorStorage,
      diag_authorization_prepared: snapshot.authorizationPrepared,
      diag_visitor_header_prepared: snapshot.visitorHeaderPrepared,
    },
    true,
  );
  if (
    snapshot.endpoint.includes('/api/cart/store') &&
    !snapshot.authorizationPrepared &&
    !snapshot.visitorHeaderPrepared
  ) {
    recordFirstAnomaly('missing_identity_prepared', {
      diag_request_id: snapshot.requestId,
      diag_endpoint: snapshot.endpoint,
      diag_token_storage: snapshot.tokenStorage,
      diag_visitor_storage: snapshot.visitorStorage,
    });
  }
  return snapshot;
}

/** 记录响应码和 trace 可用状态，供后端日志关联；不携带 trace 原值。 */
export function recordIdentityResponse(
  snapshot: IdentityRequestSnapshot | undefined,
  input: { businessCode?: number; httpStatus?: number; traceId?: string; transportError?: boolean },
): void {
  if (!snapshot) return;
  record('WebIdentityDiagnosticContext', {
    diag_stage: 'request_finished',
    diag_request_id: snapshot.requestId,
    diag_endpoint: snapshot.endpoint,
    diag_business_code: input.businessCode,
    diag_http_status: input.httpStatus,
    diag_trace_available: Boolean(input.traceId),
    // trace-id 是服务端日志关联键，不是 token、visitor-id 或用户资料。
    diag_trace_id: input.traceId,
    diag_transport_error: input.transportError === true,
    diag_token_storage: snapshot.tokenStorage,
    diag_visitor_storage: snapshot.visitorStorage,
    diag_authorization_prepared: snapshot.authorizationPrepared,
    diag_visitor_header_prepared: snapshot.visitorHeaderPrepared,
  });
  if (snapshot.endpoint.includes('/api/cart/store') && input.businessCode === 411) {
    recordFirstAnomaly('cart_411', {
      diag_request_id: snapshot.requestId,
      diag_endpoint: snapshot.endpoint,
      diag_token_storage: snapshot.tokenStorage,
      diag_visitor_storage: snapshot.visitorStorage,
      diag_authorization_prepared: snapshot.authorizationPrepared,
      diag_visitor_header_prepared: snapshot.visitorHeaderPrepared,
      diag_trace_available: Boolean(input.traceId),
    });
  }
  if (
    snapshot.endpoint.includes('/api/cart/store') &&
    (input.businessCode === 401 || input.httpStatus === 401)
  ) {
    recordFirstAnomaly('cart_auth_401', {
      diag_request_id: snapshot.requestId,
      diag_endpoint: snapshot.endpoint,
      diag_trace_available: Boolean(input.traceId),
    });
  }
}

function installStorageProbe(): void {
  if (typeof window === 'undefined') return;
  try {
    const storage = window.localStorage;
    const prototype = Object.getPrototypeOf(storage) as Storage;
    if (!originalSetItem) {
      originalSetItem = prototype.setItem;
      prototype.setItem = function setIdentityStorageItem(key: string, value: string): void {
        originalSetItem!.call(this, key, value);
        if (this === storage && isIdentityKey(key)) {
          record(
            'WebIdentityLifecycle',
            {
              diag_stage: 'storage_write',
              diag_identity: identityName(key),
              diag_presence: value ? 'present' : 'missing',
            },
            true,
          );
        }
      };
    }
    if (!originalRemoveItem) {
      originalRemoveItem = prototype.removeItem;
      prototype.removeItem = function removeIdentityStorageItem(key: string): void {
        originalRemoveItem!.call(this, key);
        if (this === storage && isIdentityKey(key)) {
          recordFirstAnomaly('identity_storage_removed', {
            diag_identity: identityName(key),
            diag_stage: 'storage_remove',
          });
        }
      };
    }
    if (!originalClear) {
      originalClear = prototype.clear;
      prototype.clear = function clearIdentityStorage(): void {
        const tokenPresent = this === storage && Boolean(storage.getItem(TOKEN_KEY));
        const visitorPresent = this === storage && Boolean(storage.getItem(VISITOR_KEY));
        originalClear!.call(this);
        if (this === storage && (tokenPresent || visitorPresent)) {
          recordFirstAnomaly('identity_storage_cleared', {
            diag_token_storage: tokenPresent ? 'present' : 'missing',
            diag_visitor_storage: visitorPresent ? 'present' : 'missing',
          });
        }
      };
    }
    window.addEventListener('storage', (event) => {
      const key = event.key;
      if (key === null) {
        recordFirstAnomaly('identity_storage_cleared_cross_tab', {
          diag_stage: 'storage_event',
        });
        return;
      }
      if (!key || !isIdentityKey(key)) return;
      record(
        'WebIdentityLifecycle',
        {
          diag_stage: 'storage_event',
          diag_identity: identityName(key),
          diag_presence: event.newValue ? 'present' : 'missing',
        },
        true,
      );
    });
  } catch {
    recordFirstAnomaly('storage_probe_install_error', { diag_stage: 'storage_probe_install' });
  }
}
